<template>
  <div>
    <!-- Desktop Sidebar -->
    <div class="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
      <div class="flex min-h-0 flex-1 flex-col bg-white border-r border-gray-200">
        <!-- Logo -->
        <div class="flex h-16 flex-shrink-0 items-center px-4 border-b border-gray-200">
          <svg class="h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          <span class="ml-2 text-lg font-semibold text-gray-900">
            DIIIG Cloud
          </span>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 px-3 py-4 space-y-6 overflow-y-auto">
          <!-- Main Section -->
          <div v-for="section in navigationSections" :key="section.title">
            <h3 v-if="section.title" class="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {{ section.title }}
            </h3>
            <div class="mt-2 space-y-1">
              <router-link
                v-for="item in section.items"
                :key="item.name"
                :to="item.to"
                :class="[
                  isActiveRoute(item.to)
                    ? 'bg-primary-50 text-primary-700 border-primary-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-transparent',
                  'group flex items-center px-3 py-2 text-sm font-medium rounded-md border-l-4 transition-colors'
                ]"
              >
                <svg
                  class="mr-3 flex-shrink-0 h-5 w-5"
                  :class="isActiveRoute(item.to) ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="item.icon" />
                </svg>
                {{ item.name }}
              </router-link>
            </div>
          </div>
        </nav>

        <!-- User info (bottom of sidebar) -->
        <div class="flex-shrink-0 border-t border-gray-200">
          <!-- Account Type Badge -->
          <div class="px-4 pt-3 pb-2">
            <span :class="[
              accountTypeBadge.bgColor,
              accountTypeBadge.textColor,
              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
            ]">
              {{ accountTypeBadge.label }}
            </span>
          </div>

          <!-- User Info - Click to go to Profile -->
          <router-link
            to="/profile"
            class="block p-4 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div class="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <span class="text-primary-600 font-medium text-sm">
                    {{ userInitials }}
                  </span>
                </div>
              </div>
              <div class="ml-3 min-w-0 flex-1">
                <p class="text-sm font-medium text-gray-900 truncate">
                  {{ authStore.userName || 'ゲスト' }}
                </p>
                <p class="text-xs text-gray-500 truncate">
                  {{ authStore.userEmail || '' }}
                </p>
              </div>
              <svg class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Mobile Sidebar -->
    <div
      v-if="isSideMenuOpen"
      class="fixed inset-0 z-overlay lg:hidden"
    >
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-gray-600 bg-opacity-75" @click="closeSideMenu"></div>

      <!-- Sidebar -->
      <div class="fixed inset-y-0 left-0 flex w-64 flex-col bg-white shadow-xl">
        <!-- Logo & Close Button -->
        <div class="flex h-16 flex-shrink-0 items-center justify-between px-4 border-b border-gray-200">
          <div class="flex items-center">
            <svg class="h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            <span class="ml-2 text-lg font-semibold text-gray-900">
              DIIIG Cloud
            </span>
          </div>
          <button
            type="button"
            class="text-gray-400 hover:text-gray-600"
            @click="closeSideMenu"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 px-3 py-4 space-y-6 overflow-y-auto">
          <div v-for="section in navigationSections" :key="section.title">
            <h3 v-if="section.title" class="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {{ section.title }}
            </h3>
            <div class="mt-2 space-y-1">
              <router-link
                v-for="item in section.items"
                :key="item.name"
                :to="item.to"
                :class="[
                  isActiveRoute(item.to)
                    ? 'bg-primary-50 text-primary-700 border-primary-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-transparent',
                  'group flex items-center px-3 py-2 text-sm font-medium rounded-md border-l-4 transition-colors'
                ]"
                @click="closeSideMenu"
              >
                <svg
                  class="mr-3 flex-shrink-0 h-5 w-5"
                  :class="isActiveRoute(item.to) ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="item.icon" />
                </svg>
                {{ item.name }}
              </router-link>
            </div>
          </div>
        </nav>

        <!-- User info (mobile) -->
        <div class="flex-shrink-0 border-t border-gray-200">
          <!-- Account Type Badge -->
          <div class="px-4 pt-3 pb-2">
            <span :class="[
              accountTypeBadge.bgColor,
              accountTypeBadge.textColor,
              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
            ]">
              {{ accountTypeBadge.label }}
            </span>
          </div>

          <!-- User Info - Click to go to Profile -->
          <router-link
            to="/profile"
            class="block p-4 hover:bg-gray-50 transition-colors cursor-pointer"
            @click="closeSideMenu"
          >
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div class="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <span class="text-primary-600 font-medium text-sm">
                    {{ userInitials }}
                  </span>
                </div>
              </div>
              <div class="ml-3 min-w-0 flex-1">
                <p class="text-sm font-medium text-gray-900 truncate">
                  {{ authStore.userName || 'ゲスト' }}
                </p>
                <p class="text-xs text-gray-500 truncate">
                  {{ authStore.userEmail || '' }}
                </p>
              </div>
              <svg class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'
import { useUiStore } from '../stores/ui.js'

const route = useRoute()
const authStore = useAuthStore()
const uiStore = useUiStore()

const isSideMenuOpen = computed(() => uiStore.isSideMenuOpen)

const closeSideMenu = () => {
  uiStore.closeSideMenu()
}

const userInitials = computed(() => {
  const name = authStore.userName
  if (!name) return 'U'
  return name.split(' ').map(part => part[0]).join('').toUpperCase().slice(0, 2)
})

// Account type badge
const accountTypeBadge = computed(() => {
  const accountType = authStore.user?.account_type || 'trial'

  switch (accountType) {
    case 'trial':
      return {
        label: 'トライアル',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800'
      }
    case 'individual':
      return {
        label: '個人アカウント',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800'
      }
    case 'corporate':
      return {
        label: '法人アカウント',
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-800'
      }
    default:
      return {
        label: 'アカウント',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800'
      }
  }
})

// Navigation sections with icons (SVG path strings)
const navigationSections = computed(() => {
  const sections = [
    {
      title: null, // No title for main section
      items: [
        {
          name: 'ダッシュボード',
          to: '/dashboard',
          icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
        }
      ]
    },
    {
      title: 'API管理',
      items: [
        {
          name: 'APIキー',
          to: '/api-keys',
          icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z'
        },
        {
          name: '使用量',
          to: '/usage',
          icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
        }
      ]
    },
    {
      title: 'データ管理',
      items: [
        {
          name: 'POIデータセット',
          to: '/datasets',
          icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z'
        },
        {
          name: 'POI',
          to: '/poi',
          icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z'
        },
        {
          name: '公開データセット',
          to: '/marketplace',
          icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z'
        }
      ]
    },
    {
      title: '請求・設定',
      items: [
        {
          name: '請求・支払い',
          to: '/billing',
          icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01'
        },
        {
          name: '支払い方法',
          to: '/payment-method',
          icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z'
        },
        {
          name: 'プロフィール',
          to: '/profile',
          icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
        }
      ]
    }
  ]

  // Add data provider section if user is data provider
  if (authStore.userType === 'data_provider' || authStore.isAdmin) {
    sections.push({
      title: 'データ提供者',
      items: [
        {
          name: '収益レポート',
          to: '/revenue',
          icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
        }
      ]
    })
  }

  // Add admin section if user is admin
  if (authStore.isAdmin) {
    sections.push({
      title: '管理者機能',
      items: [
        {
          name: 'ユーザー管理',
          to: '/admin/users',
          icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z'
        },
        {
          name: '法人アカウント',
          to: '/admin/corporate',
          icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
        },
        {
          name: '振込確認',
          to: '/admin/transfers',
          icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z'
        },
        {
          name: '収益集計',
          to: '/admin/revenue',
          icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z'
        },
        {
          name: '価格設定',
          to: '/admin/pricing',
          icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z'
        }
      ]
    })
  }

  return sections
})

const isActiveRoute = (to) => {
  if (to === '/dashboard') {
    return route.path === '/dashboard'
  }
  return route.path.startsWith(to)
}
</script>
